import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { dashboardAPI, placementsAPI, tasksAPI } from '../lib/api';
import { formatDate } from '../lib/dateUtils';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import TaskModal from '../components/TaskModal';
import PlacementDetailsDialog from '../components/PlacementDetailsDialog';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Users, Building2, Calendar, CheckCircle, Clock, MapPin, FileSignature, AlertTriangle, Plus, CheckCircle2, ListTodo } from 'lucide-react';
import { toast } from 'sonner';

const PRIORITY_DOT = { low: 'bg-cyan-500', medium: 'bg-amber-500', high: 'bg-red-500' };

const Dashboard = () => {
  const { user, isReadOnly } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentPlacements, setRecentPlacements] = useState([]);
  const [taskBuckets, setTaskBuckets] = useState({ overdue: [], today: [], soon: [] });
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [detailsPlacementId, setDetailsPlacementId] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, placementsRes, bucketsRes] = await Promise.all([
        dashboardAPI.getStats(),
        placementsAPI.getAll(),
        tasksAPI.buckets(),
      ]);
      setStats(statsRes.data);
      setRecentPlacements(placementsRes.data.slice(0, 5));
      setTaskBuckets(bucketsRes.data || { overdue: [], today: [], soon: [] });
      window.dispatchEvent(new Event('tasks-changed'));
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteTask = async (e, taskId) => {
    e.stopPropagation();
    try {
      await tasksAPI.complete(taskId);
      toast.success('Task completed');
      fetchData();
    } catch {
      toast.error('Failed to complete task');
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      active: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      pending: 'bg-amber-100 text-amber-700 border-amber-200',
      completed: 'bg-blue-100 text-blue-700 border-blue-200',
      cancelled: 'bg-red-100 text-red-700 border-red-200',
    };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${variants[status] || variants.pending}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ica-navy"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="dashboard-page" className="space-y-8 animate-fade-in">
        {/* Header */}
        <div>
          <h1 className="font-heading text-3xl font-bold text-ica-navy">
            Welcome back, {user?.name?.split(' ')[0]}
          </h1>
          <p className="text-gray-500 mt-1">Here's what's happening with your placements today</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card data-testid="stat-students" onClick={() => navigate('/students')} className="border-t-4 border-t-ica-navy cursor-pointer hover:shadow-lg hover:border-ica-navy hover:-translate-y-0.5 transition-all">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 font-medium">Total Students</p>
                  <p className="text-3xl font-heading font-bold text-ica-navy mt-1">
                    {stats?.total_students || 0}
                  </p>
                </div>
                <div className="w-12 h-12 bg-ica-navy/10 rounded-xl flex items-center justify-center">
                  <Users className="w-6 h-6 text-ica-navy" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="stat-restaurants" onClick={() => navigate('/restaurants')} className="border-t-4 border-t-ica-gold cursor-pointer hover:shadow-lg hover:border-ica-gold hover:-translate-y-0.5 transition-all">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 font-medium">Partner Restaurants</p>
                  <p className="text-3xl font-heading font-bold text-ica-gold mt-1">
                    {stats?.total_restaurants || 0}
                  </p>
                </div>
                <div className="w-12 h-12 bg-ica-gold/10 rounded-xl flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-ica-gold" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="stat-active" onClick={() => navigate('/placements?status=active')} className="border-t-4 border-t-emerald-500 cursor-pointer hover:shadow-lg hover:border-emerald-500 hover:-translate-y-0.5 transition-all">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 font-medium">Active Placements</p>
                  <p className="text-3xl font-heading font-bold text-emerald-600 mt-1">
                    {stats?.active_placements || 0}
                  </p>
                </div>
                <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="stat-positions" onClick={() => navigate('/restaurants?filter=open-positions')} className="border-t-4 border-t-sky-500 cursor-pointer hover:shadow-lg hover:border-sky-500 hover:-translate-y-0.5 transition-all">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 font-medium">Available Positions</p>
                  <p className="text-3xl font-heading font-bold text-sky-600 mt-1">
                    {stats?.available_positions || 0}
                  </p>
                </div>
                <div className="w-12 h-12 bg-sky-100 rounded-xl flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-sky-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Secondary Stats — 6-up: covers every stat the API returns so every
            number the admin sees is a one-click drill-down. */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <Card data-testid="stat-pending" onClick={() => navigate('/placements?status=pending')} className="cursor-pointer hover:shadow-md hover:border-ica-navy transition-all">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Clock className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-heading font-bold text-gray-900">
                    {stats?.pending_placements || 0}
                  </p>
                  <p className="text-sm text-gray-500">Pending Placements</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="stat-completed" onClick={() => navigate('/placements?status=completed')} className="cursor-pointer hover:shadow-md hover:border-ica-navy transition-all">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-heading font-bold text-gray-900">
                    {stats?.completed_placements || 0}
                  </p>
                  <p className="text-sm text-gray-500">Completed Placements</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="stat-pending-acks" onClick={() => navigate('/placements?ack=pending')} className="cursor-pointer hover:shadow-md hover:border-ica-navy transition-all">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <FileSignature className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-heading font-bold text-gray-900">
                    {stats?.pending_acknowledgements || 0}
                  </p>
                  <p className="text-sm text-gray-500">Pending Acks</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="stat-overdue" onClick={() => navigate('/placement-feedback?filter=overdue')} className="cursor-pointer hover:shadow-md hover:border-ica-navy transition-all">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-2xl font-heading font-bold text-gray-900">
                    {stats?.overdue_assessments || 0}
                  </p>
                  <p className="text-sm text-gray-500">Overdue Assessments</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="stat-tasks-overdue" onClick={() => navigate('/tasks?filter=overdue')} className="cursor-pointer hover:shadow-md hover:border-ica-navy transition-all">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <ListTodo className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-2xl font-heading font-bold text-gray-900">
                    {stats?.tasks_overdue || 0}
                  </p>
                  <p className="text-sm text-gray-500">Tasks Overdue</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="stat-tasks-today" onClick={() => navigate('/tasks?filter=today')} className="cursor-pointer hover:shadow-md hover:border-ica-navy transition-all">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <ListTodo className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-heading font-bold text-gray-900">
                    {stats?.tasks_due_today || 0}
                  </p>
                  <p className="text-sm text-gray-500">Tasks Today</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Placements — placed BEFORE My Tasks per user request */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading text-xl text-ica-navy">Recent Placements</CardTitle>
          </CardHeader>
          <CardContent>
            {recentPlacements.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No placements yet. Create your first placement to get started.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 text-xs font-semibold text-ica-navy uppercase tracking-wider">Student</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-ica-navy uppercase tracking-wider">Restaurant</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-ica-navy uppercase tracking-wider">Position</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-ica-navy uppercase tracking-wider">Period</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-ica-navy uppercase tracking-wider">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentPlacements.map((placement) => (
                      <tr
                        key={placement.id}
                        data-testid={`placement-row-${placement.id}`}
                        onClick={() => setDetailsPlacementId(placement.id)}
                        className="border-b hover:bg-ica-gold/5 transition-colors cursor-pointer"
                        title="View placement details"
                      >
                        <td className="py-4 px-4 font-medium text-gray-900">{placement.student_name}</td>
                        <td className="py-4 px-4 text-gray-600">{placement.restaurant_name}</td>
                        <td className="py-4 px-4 text-gray-600">{placement.position}</td>
                        <td className="py-4 px-4 text-gray-500 text-sm">
                          {formatDate(placement.start_date)} - {formatDate(placement.end_date)}
                        </td>
                        <td className="py-4 px-4">{getStatusBadge(placement.status)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* My Tasks — moved below Recent Placements */}
        <Card data-testid="my-tasks-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <div className="flex items-center gap-2">
              <ListTodo className="w-5 h-5 text-ica-navy" />
              <CardTitle className="font-heading text-xl text-ica-navy">My Tasks</CardTitle>
            </div>
            <div className="flex items-center gap-2">
              {!isReadOnly && (
                <Button
                  data-testid="dashboard-new-task-btn"
                  size="sm"
                  onClick={() => { setEditingTask(null); setTaskModalOpen(true); }}
                  
                >
                  <Plus className="w-4 h-4 mr-1" /> New Task
                </Button>
              )}
              <Button
                size="sm"
                variant="outline"
                onClick={() => navigate('/placement-calendar')}
               
              >
                Open Calendar
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {taskBuckets.overdue.length === 0 && taskBuckets.today.length === 0 && taskBuckets.soon.length === 0 ? (
              <div className="text-center py-10 text-gray-500">
                <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-500 mb-2" />
                <p className="font-medium text-gray-700">All clear!</p>
                <p className="text-sm">You have no open tasks. Click <strong>New Task</strong> to add a follow-up.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <TaskBucket
                  testid="bucket-overdue"
                  label="Overdue"
                  tone="red"
                  tasks={taskBuckets.overdue}
                  onItemClick={(t) => { setEditingTask(t); setTaskModalOpen(true); }}
                  onComplete={handleCompleteTask}
                />
                <TaskBucket
                  testid="bucket-today"
                  label="Due Today"
                  tone="gold"
                  tasks={taskBuckets.today}
                  onItemClick={(t) => { setEditingTask(t); setTaskModalOpen(true); }}
                  onComplete={handleCompleteTask}
                />
                <TaskBucket
                  testid="bucket-soon"
                  label="Due Soon"
                  tone="slate"
                  tasks={taskBuckets.soon}
                  onItemClick={(t) => { setEditingTask(t); setTaskModalOpen(true); }}
                  onComplete={handleCompleteTask}
                />
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      <TaskModal
        open={taskModalOpen}
        onOpenChange={setTaskModalOpen}
        task={editingTask}
        onSaved={() => fetchData()}
        onDeleted={() => fetchData()}
      />
      <PlacementDetailsDialog
        open={!!detailsPlacementId}
        onOpenChange={(o) => { if (!o) setDetailsPlacementId(null); }}
        placementId={detailsPlacementId}
      />
    </Layout>
  );
};

const TaskBucket = ({ testid, label, tone, tasks, onItemClick, onComplete }) => {
  const tones = {
    red: { header: 'bg-red-50 text-red-700 border-red-200', dot: 'bg-red-500' },
    gold: { header: 'bg-ica-gold/10 text-ica-gold-dark border-ica-gold/30', dot: 'bg-ica-gold' },
    slate: { header: 'bg-slate-50 text-slate-700 border-slate-200', dot: 'bg-slate-400' },
  }[tone] || { header: 'bg-gray-50 text-gray-700 border-gray-200', dot: 'bg-gray-400' };
  return (
    <div data-testid={testid} className="border rounded-lg overflow-hidden">
      <div className={`flex items-center justify-between px-3 py-2 border-b text-xs font-semibold uppercase tracking-wider ${tones.header}`}>
        <span className="inline-flex items-center gap-2">
          <span className={`inline-block w-2 h-2 rounded-full ${tones.dot}`} />
          {label}
        </span>
        <span className="text-[10px] opacity-70">{tasks.length}</span>
      </div>
      <ul className="divide-y max-h-72 overflow-y-auto">
        {tasks.length === 0 ? (
          <li className="px-3 py-6 text-center text-xs text-gray-400">— none —</li>
        ) : tasks.map((t) => (
          <li
            key={t.id}
            data-testid={`task-item-${t.id}`}
            onClick={() => onItemClick(t)}
            className="px-3 py-2 hover:bg-ica-gold/5 cursor-pointer flex items-start gap-2 transition-colors"
          >
            <span className={`mt-1.5 inline-block w-1.5 h-1.5 rounded-full flex-shrink-0 ${PRIORITY_DOT[t.priority] || 'bg-gray-400'}`} />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">{t.title}</p>
              <p className="text-[11px] text-gray-500 truncate">
                {t.due_date}
                {t.related_label ? <> · {t.related_label}</> : null}
              </p>
            </div>
            <button
              data-testid={`complete-task-${t.id}`}
              onClick={(e) => onComplete(e, t.id)}
              className="text-gray-300 hover:text-emerald-600 transition-colors"
              title="Mark as done"
            >
              <CheckCircle2 className="w-4 h-4" />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default Dashboard;
